import * as React from 'react';
import { Clock, Info, Shield } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { StepGuide, WorkspaceButton, stepStates, type GuideStep } from '@/components/ui/workspace';
import {
  reviewCount,
  useKbImports,
  useKbNames,
  useKbReview,
  useKbSettings,
  useKbSummary,
  type KbImportRun,
  type KbRecentRun,
  type KbReview,
  type KbSummary,
} from '@/lib/api/vendor-knowledge';
import { Note, Page, PageHead } from './kb-bits';
import { fmt, formatDate, outcomeText, plural, reviewAgeText } from './kb-logic';
import { useKb } from './vendor-knowledge-screen';

/**
 * Home, with nothing selected (prototype `homeView()`): what the knowledge
 * base is, where to start (curators), its health and the latest checks.
 * Every number is the server's: `GET /summary`, `GET /review`,
 * `GET /imports`. Nothing here is estimated.
 */
export function KbHome() {
  const { canCurate } = useKb();
  const summary = useKbSummary({});
  const review = useKbReview(null, canCurate);
  const imports = useKbImports(canCurate);
  const settings = useKbSettings();

  return (
    <Page>
      <PageHead title="Vendor knowledge base">
        What Caliber knows about each vendor’s products. Every item quotes its source, and
        future-state maps cite it.
      </PageHead>
      {summary.isPending ? (
        <div className="mt-8">
          <Spinner />
        </div>
      ) : summary.data ? (
        <>
          {canCurate ? (
            <Guide summary={summary.data} review={review.data} imports={imports.data?.runs} />
          ) : null}
          <Health
            summary={summary.data}
            review={canCurate ? review.data : undefined}
            reviewAfterDays={settings.data?.review_after_days}
          />
          {canCurate && summary.data.recent ? <Recent runs={summary.data.recent} /> : null}
        </>
      ) : null}
    </Page>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
      <h2 className="m-0 text-h2 font-semibold text-ink-900">{title}</h2>
      {hint ? <span className="text-meta text-ink-500">{hint}</span> : null}
    </div>
  );
}

/** The records stage of the latest completed import, if any. */
function lastImport(runs: KbImportRun[] | undefined): KbImportRun | null {
  return runs?.find((r) => r.stage === 'records' && r.status === 'completed') ?? null;
}

function n(report: Record<string, unknown>, key: string): number {
  const v = report[key];
  return typeof v === 'number' ? v : 0;
}

function Guide({
  summary,
  review,
  imports,
}: {
  summary: KbSummary;
  review: KbReview | undefined;
  imports: KbImportRun[] | undefined;
}) {
  const { tree, navigate, scrollTo } = useKb();
  const names = useKbNames();
  const pendingNames = (names.data?.import ?? []).filter((n) => n.status === 'pending').length;
  const imported = lastImport(imports);
  const held = reviewCount(review, 'held');
  const changes = reviewCount(review, 'proposed') + reviewCount(review, 'flagged');
  const pages = summary.web_pages;
  const topVendor = (review?.by_vendor ?? [])
    .map((v) => tree.vendors.find((t) => t.id === v.vendor_id))
    .find((v) => v !== undefined);

  const openReview = (vendorId: string) => {
    navigate({ kind: 'vendor', id: vendorId });
    scrollTo('kb-to-review');
  };

  const importReport = imported?.report ?? {};
  const byKind = (importReport['items_by_kind'] ?? {}) as Record<string, number>;
  const answers = byKind['requirement_response'] ?? 0;
  const items =
    Object.values(byKind).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0) - answers;

  const done = [
    imported !== null,
    held === 0 && pendingNames === 0,
    !!pages && pages.registered > 0 && pages.checked >= pages.registered,
    changes === 0,
  ];
  const states = stepStates(done);
  const steps: GuideStep[] = [
    {
      key: 'import',
      title: 'Import from Caliber v1',
      state: states[0]!,
      body: imported
        ? `${plural(n(importReport, 'vendors'), 'vendor')}, ${plural(n(importReport, 'partners'), 'implementation partner')}, ${plural(items, 'item')} and ${plural(answers, 'requirement answer')}, cited as Avero Advisors Vendor Knowledge Base.`
        : 'The import script reads Caliber v1’s export file: vendors, partners, items and requirement answers.',
    },
    {
      key: 'names',
      title: 'Check client names',
      state: states[1]!,
      body:
        pendingNames > 0
          ? `${plural(pendingNames, 'name')} from Caliber v1’s project list ${pendingNames === 1 ? 'waits' : 'wait'} for a decision: approve a client name, reject an ordinary word.${held > 0 ? ` Then ${plural(held, 'item')} held for a look.` : ''}`
          : held > 0
            ? `${plural(held, 'item')} ${held === 1 ? 'is' : 'are'} held for a look: a client name removed, a rating from Caliber v1, or a proposal assessment. Each needs a look before it can be confirmed.`
            : 'Every name and held item has been looked at.',
      note: (
        <>
          <Shield className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
          <span>
            Code checks every import, AI proposal and edit against the protected names in settings,
            before anything is stored.
          </span>
        </>
      ),
      action:
        pendingNames > 0 ? (
          <WorkspaceButton
            variant="primary"
            onClick={() => {
              navigate({ kind: 'settings' });
              scrollTo('kb-import-names');
            }}
          >
            Decide the names
          </WorkspaceButton>
        ) : topVendor ? (
          <WorkspaceButton variant="primary" onClick={() => openReview(topVendor.id)}>
            Open {topVendor.name}
          </WorkspaceButton>
        ) : undefined,
    },
    {
      key: 'pages',
      title: 'Capture vendor web pages',
      state: states[2]!,
      body: pages
        ? pages.registered > 0
          ? `${fmt(pages.checked)} of ${plural(pages.registered, 'web page')} captured. A first check captures each page, so items can be confirmed against the vendor’s own words, or flagged where the page no longer says them. Pages from Caliber v1 wait until you approve their vendor’s web domain, on the vendor’s page.`
          : 'No web pages yet. Add a vendor’s pages from its page, under Sources.'
        : '',
      note: (
        <>
          <Info className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
          <span>
            Reading a page uses the firm AI budget. Later checks of unchanged pages cost nothing.
          </span>
        </>
      ),
    },
    {
      key: 'review',
      title: 'Review what changed',
      state: states[3]!,
      body:
        changes > 0
          ? `New passages, changed pages and pages that are gone: ${plural(changes, 'thing')} to review for ${plural(review?.by_vendor.length ?? 0, 'vendor')}, each in its vendor’s To review.`
          : 'Nothing from the checks is waiting.',
      action:
        topVendor && changes > 0 ? (
          <WorkspaceButton variant="primary" onClick={() => openReview(topVendor.id)}>
            Open {topVendor.name}
          </WorkspaceButton>
        ) : undefined,
    },
  ];

  return (
    <section aria-label="Where to start" className="mt-7">
      <SectionTitle
        title="Where to start"
        hint="Do the step in orange. Each step ticks itself when its work is done."
      />
      <StepGuide aria-label="Where to start" steps={steps} />
    </section>
  );
}

function Cell({ label, value, sub }: { label: string; value: number; sub?: React.ReactNode }) {
  return (
    <div className="bg-paper-0 px-4 py-3.5">
      <div className="text-meta font-medium text-ink-500">{label}</div>
      <div className="mt-0.5 text-[22px] font-semibold leading-[30px] tracking-[-0.01em] tabular-nums text-ink-900">
        {fmt(value)}
      </div>
      {sub ? <div className="text-meta text-ink-500">{sub}</div> : null}
    </div>
  );
}

function Bn({ children }: { children: number }) {
  return <b className="font-semibold tabular-nums text-ink-700">{fmt(children)}</b>;
}

function Health({
  summary,
  review,
  reviewAfterDays,
}: {
  summary: KbSummary;
  review: KbReview | undefined;
  reviewAfterDays: number | undefined;
}) {
  const waiting = reviewCount(review, 'proposed') + reviewCount(review, 'held');
  const due = reviewCount(review, 'due_for_review');
  const cells = [
    <Cell
      key="items"
      label="Knowledge items"
      value={summary.knowledge_items}
      sub={
        review ? (
          <>
            confirmed · <Bn>{waiting}</Bn> waiting for review ·{' '}
            <Bn>{reviewCount(review, 'flagged')}</Bn> flagged
          </>
        ) : (
          'confirmed'
        )
      }
    />,
    <Cell
      key="answers"
      label="Requirement answers"
      value={summary.requirement_answers}
      sub={`from ${plural(summary.answer_partners, 'implementation partner')}`}
    />,
  ];
  if (summary.web_pages) {
    cells.push(
      <Cell
        key="pages"
        label="Web pages"
        value={summary.web_pages.checked}
        sub={
          <>
            checked · <Bn>{summary.web_pages.due}</Bn> due · <Bn>{summary.web_pages.gone}</Bn> gone
          </>
        }
      />,
    );
  }
  if (summary.documents) {
    cells.push(
      <Cell
        key="docs"
        label="Documents"
        value={summary.documents.total}
        sub={
          <>
            {fmt(summary.documents.confidential)} vendor-confidential ·{' '}
            <Bn>{reviewCount(review, 'received')}</Bn> waiting for you
          </>
        }
      />,
    );
  }
  return (
    <section aria-label="Health" className="mt-8 border-t border-ink-100 pt-6">
      <SectionTitle
        title="Health"
        hint="Counted from the knowledge base. Nothing here is estimated."
      />
      <div
        className={
          cells.length === 4
            ? 'grid grid-cols-1 gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 2xl:grid-cols-4'
            : 'grid grid-cols-1 gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2'
        }
      >
        {cells}
      </div>
      {review && reviewAfterDays !== undefined ? (
        <div className="mt-2.5 flex items-start gap-2 text-body-sm text-ink-700">
          <Clock className="mt-[3px] h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
          <span>
            {due > 0
              ? `${plural(due, 'item')} ${due === 1 ? 'is' : 'are'} older than ${reviewAgeText(reviewAfterDays)}, the review age set in settings. Each is listed under its vendor’s To review.`
              : `No item is older than ${reviewAgeText(reviewAfterDays)}, the review age set in settings.`}
          </span>
        </div>
      ) : null}
    </section>
  );
}

/** One finished check or reading, in words: what it found, by code. */
export function recentText(run: KbRecentRun): string {
  const title = run.source.title ?? run.source.url ?? 'a source';
  if (run.status === 'failed')
    return `Could not finish ${run.kind === 'check' ? 'checking' : 'reading'} ${title}`;
  const found = [
    run.counts.proposed > 0 ? plural(run.counts.proposed, 'passage') + ' proposed' : null,
    run.counts.offered_citations > 0
      ? plural(run.counts.offered_citations, 'further citation') + ' offered'
      : null,
    run.counts.flagged > 0 ? plural(run.counts.flagged, 'item') + ' flagged' : null,
  ].filter(Boolean);
  const verb = run.kind === 'check' ? 'Checked' : 'Read';
  const what = run.kind === 'check' ? `: ${outcomeText(run.outcome)}` : '';
  return `${verb} ${title}${what}${found.length ? `, ${found.join(', ')}` : ''}`;
}

function Recent({ runs }: { runs: KbRecentRun[] }) {
  return (
    <section aria-label="Recent" className="mt-8 border-t border-ink-100 pt-6">
      <SectionTitle title="Recent" />
      {runs.length === 0 ? (
        <Note icon={<Info className="h-3 w-3" />}>No page or document has been checked yet.</Note>
      ) : (
        <ul className="m-0 list-none border-t border-ink-100 p-0">
          {runs.map((r) => (
            <li
              key={r.run_id}
              className="flex items-baseline gap-4 border-b border-ink-100 py-[9px] text-body-sm text-ink-700"
            >
              <span className="min-w-0 flex-1">
                {r.vendor ? (
                  <>
                    <b className="font-semibold text-ink-900">{r.vendor.name}</b> ·{' '}
                  </>
                ) : null}
                {recentText(r)}
              </span>
              <span className="flex-none text-meta text-ink-500">{formatDate(r.finished_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
